import React, { useState, useRef, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from 'react-i18next';
import { setI18nLanguage } from '../../i18n/config';
import i18n from '../../i18n/config';

const Section = ({ title, children }) => (
  <div className="bg-white border border-gray-200 rounded-xl px-3.5 py-3 mb-3.5">
    {title && (
      <p className="text-sm font-semibold text-gray-800 mb-2.5">{title}</p>
    )}
    <div className="flex flex-col gap-2">{children}</div>
  </div>
);

const Btn = ({ label, onClick, danger }) => (
  <button
    onClick={onClick}
    className={`w-full text-left rounded-lg px-4 py-2.5 text-sm font-medium text-white transition ${
      danger ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-600 hover:bg-blue-700'
    }`}
  >
    {label}
  </button>
);

const SrClerkDashboard = ({
  ngo,
  onHome,
  onBack,
  onContributions,
  onAddExpenses,
  onDeleteTransaction,
  onMyDeletionRequests,
  onTransactions,
  onApproveExpense,
  onReports,
  onNotifications,
  onEditProfile, // pass this down from the parent, same as Contributions
}) => {
  const { user, logout } = useAuth0();
  const { t } = useTranslation('dashboards');
  const { t: tc } = useTranslation('common');

  // ---- Profile dropdown state ----
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // ---- Language menu state ----
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);

  useEffect(() => {
    if (!profileOpen) return;
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen]);

  // Close the language menu on outside click.
  useEffect(() => {
    if (!langMenuOpen) return;
    const handleClickOutside = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [langMenuOpen]);

  const handleLogout = () => {
    setProfileOpen(false);
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleEditProfile = () => {
    setProfileOpen(false);
    if (onEditProfile) onEditProfile();
  };

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center gap-3">
        {/* Home icon — 20x20, filled house glyph */}
        <button onClick={onHome} className="text-blue-600 hover:text-blue-700 flex-shrink-0" aria-label="Home" title="Home">
          <svg width="20" height="20" viewBox="0 0 24 24" className="fill-current">
            <path d="M12 3.2 3 10v10h6v-6h6v6h6V10l-9-6.8Z" />
          </svg>
        </button>

        {/* Back arrow — 22x22, outlined */}
        <button onClick={onBack} aria-label="Back" className="text-blue-600 hover:text-blue-700">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M5 12l7-7M5 12l7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {/* Top-bar label */}
        <span className="text-sm font-medium text-gray-900 flex-1">{t('srClerk.title')}</span>

        <div className="flex-1" />

        {/* ---- LANGUAGE TOGGLE ---- */}
        <div ref={langMenuRef} className="relative">
          <button
            onClick={() => setLangMenuOpen((open) => !open)}
            aria-label="Language menu"
            aria-expanded={langMenuOpen}
            className="text-xs font-medium text-gray-600 border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 transition"
          >
            {i18n.language === 'ml' ? 'മലയാളം' : 'EN'}
          </button>
          {langMenuOpen && (
            <div className="absolute right-0 top-10 w-32 bg-white rounded-xl shadow-lg border border-gray-100 p-2 z-50">
              <button
                onClick={() => {
                  const lang = 'en';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'en' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                English
              </button>
              <button
                onClick={() => {
                  const lang = 'ml';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'ml' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                മലയാളം
              </button>
            </div>
          )}
        </div>

        {/* Profile avatar button + dropdown */}
        <div ref={profileRef} className="relative">
          <button
            onClick={() => setProfileOpen((open) => !open)}
            aria-label="Account menu"
            aria-expanded={profileOpen}
            className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium"
          >
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </button>

          {profileOpen && (
            <div className="absolute right-0 top-11 w-64 bg-white rounded-xl shadow-lg border border-gray-100 p-4 z-50">
              <p className="text-sm text-gray-900 truncate">{user?.email}</p>
              <p className="text-sm text-gray-400 truncate mb-4">{user?.email}</p>

              <button
                onClick={handleEditProfile}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition mb-2"
              >
                {tc('editProfile')}
              </button>
              <button
                onClick={handleLogout}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition"
              >
                {tc('logout')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Page content */}
      <div className="max-w-sm mx-auto px-4 py-8">

        {/* Body heading — §2 */}
        <h1 className="text-center text-base font-medium text-blue-600 mb-6 uppercase tracking-wider">
          {t('srClerk.title')}
        </h1>

        {/* Financial Overview */}
        <Section title={t('srClerk.financialOverview')}>
          <Btn label={t('srClerk.contributions')} onClick={onContributions} />
          <Btn label={t('srClerk.addExpenses')} onClick={onAddExpenses} />
        </Section>

        {/* Operations */}
        <Section title={t('srClerk.operations')}>
          <Btn label={t('srClerk.requestTransactionDelete')} onClick={onDeleteTransaction} danger />
          <Btn label={t('srClerk.myDeletionRequests')} onClick={onMyDeletionRequests} />
          <Btn label={t('srClerk.transactions')} onClick={onTransactions} />
        </Section>

        {/* Approvals */}
        <Section title={t('srClerk.approvals')}>
          <Btn label={t('srClerk.approveOrReject')} onClick={onApproveExpense} />
        </Section>

        {/* Insights */}
        <Section title={t('srClerk.insights')}>
          <Btn label={t('srClerk.reports')} onClick={onReports} />
          <Btn label={t('srClerk.notifications')} onClick={onNotifications} />
        </Section>

      </div>
    </div>
  );
};

export default SrClerkDashboard;